import { useState } from "react";
import { X, ShieldCheck, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency } from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";

/**
 * Generates the Massachusetts Limited Used Vehicle Warranty PDF for a sold
 * vehicle. The dealer/owner signature is pre-printed onto the PDF from the
 * dealership profile (saved in Settings). The customer signs the printed
 * paper after the sales rep walks them through the warranty, so no canvas
 * is rendered in this UI.
 *
 * Props:
 *   - vehicle: vehicle object (will pre-fill VIN/make/model/mileage/buyer)
 *   - onClose: () => void
 */
export default function LemonLawWarrantyModal({ vehicle, onClose }) {
  const [busy, setBusy] = useState(false);
  // Output language for the legal PDF. Defaults to Portuguese since this is
  // the dealership's primary language; can also be English or Spanish so the
  // buyer receives a document in a language they read.
  const [language, setLanguage] = useState("pt");
  const [form, setForm] = useState({
    buyer_name: vehicle?.buyer_name || "",
    buyer_phone: vehicle?.buyer_phone || "",
    buyer_email: vehicle?.buyer_email || "",
    buyer_address: vehicle?.buyer_address || "",
    sale_price: vehicle?.sold_price || 0,
    sale_date: (vehicle?.sold_at || new Date().toISOString()).slice(0, 10),
    // Editable mileage — dealers test-drive cars so the delivery mileage is
    // often different from the inventory value. This number drives the Term
    // of Protection tier.
    mileage: vehicle?.mileage || 0,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Term-of-protection preview based on mileage at sale (label localizes
  // with the chosen output language). Uses the editable form.mileage value
  // so the salesperson can adjust after the test-drive at delivery time.
  const mileage = Number(form.mileage || 0);
  const term = (() => {
    const labels = {
      pt: [
        "90 dias OU 3.750 milhas",
        "60 dias OU 2.500 milhas",
        "30 dias OU 1.250 milhas",
        "NÃO COBERTO (125.000+ milhas)",
      ],
      en: [
        "90 days OR 3,750 miles",
        "60 days OR 2,500 miles",
        "30 days OR 1,250 miles",
        "NOT COVERED (125,000+ miles)",
      ],
      es: [
        "90 días O 3.750 millas",
        "60 días O 2.500 millas",
        "30 días O 1.250 millas",
        "NO CUBIERTO (125.000+ millas)",
      ],
    }[language] || [];
    if (mileage < 40000) return { label: labels[0], color: "text-success", eligible: true };
    if (mileage < 80000) return { label: labels[1], color: "text-success", eligible: true };
    if (mileage < 125000) return { label: labels[2], color: "text-success", eligible: true };
    return { label: labels[3], color: "text-warning", eligible: false };
  })();

  // Short labels for the UI itself — switch with the selected output language
  const L = ({
    pt: {
      header_eyebrow: "Pacote de Entrega (MA Lemon Law)",
      header_title: "Documentos da Entrega",
      header_desc: "Gera 6 páginas em um só PDF: Garantia Lemon Law (3 págs), Odômetro Federal, Buyer's Guide FTC (frente e verso). Tudo com os mesmos dados.",
      vehicle: "Veículo", mileage: "Milhas", coverage: "Cobertura",
      not_covered_warn: "⚠️ Este veículo tem 125.000+ milhas e não é coberto pela Lemon Law estadual. Você ainda pode gerar o documento informando o comprador sobre essa exclusão.",
      buyer_name: "Nome do comprador", phone: "Telefone", email: "Email",
      address: "Endereço", sale_price: "Preço de venda", sale_date: "Data da venda",
      print_hint: "O pacote sai com a assinatura do dono já estampada. Explique a garantia e os documentos ao cliente e peça para ele assinar no papel nas linhas indicadas (Garantia, Odômetro e Recibo do Buyer's Guide).",
      cancel: "Cancelar", generate: "Gerar Pacote (6 págs)", generating: "Gerando...",
      lang_label: "Idioma da garantia",
      need_name: "Preencha o nome do comprador",
      ok: "Pacote gerado!", err: "Erro ao gerar o pacote",
    },
    en: {
      header_eyebrow: "Delivery Package (MA Lemon Law)",
      header_title: "Delivery Documents",
      header_desc: "Generates 6 pages in one PDF: Lemon Law Warranty (3 pgs), Federal Odometer Disclosure, FTC Buyer's Guide (front & back). All with the same data.",
      vehicle: "Vehicle", mileage: "Mileage", coverage: "Coverage",
      not_covered_warn: "⚠️ This vehicle has 125,000+ miles and is NOT covered by the state Lemon Law. You can still generate the document to inform the buyer of the exclusion.",
      buyer_name: "Buyer name", phone: "Phone", email: "Email",
      address: "Address", sale_price: "Sale price", sale_date: "Date of sale",
      print_hint: "The package prints with the owner's signature already on it. Walk the customer through the warranty and documents and have them sign on the printed paper at the indicated lines (Warranty, Odometer, and Buyer's Guide Receipt).",
      cancel: "Cancel", generate: "Generate Package (6 pgs)", generating: "Generating...",
      lang_label: "Warranty language",
      need_name: "Please enter the buyer's name",
      ok: "Package generated!", err: "Error generating the package",
    },
    es: {
      header_eyebrow: "Paquete de Entrega (MA Lemon Law)",
      header_title: "Documentos de Entrega",
      header_desc: "Genera 6 páginas en un PDF: Garantía Lemon Law (3 págs), Divulgación Federal del Odómetro, Buyer's Guide FTC (frente y dorso). Todo con los mismos datos.",
      vehicle: "Vehículo", mileage: "Millas", coverage: "Cobertura",
      not_covered_warn: "⚠️ Este vehículo tiene 125.000+ millas y NO está cubierto por la Lemon Law estatal. Aún puede generar el documento informando al comprador de la exclusión.",
      buyer_name: "Nombre del comprador", phone: "Teléfono", email: "Email",
      address: "Dirección", sale_price: "Precio de venta", sale_date: "Fecha de venta",
      print_hint: "El paquete se imprime con la firma del dueño ya estampada. Explique la garantía y los documentos al cliente y pídale que firme en el papel impreso en las líneas indicadas (Garantía, Odómetro y Recibo del Buyer's Guide).",
      cancel: "Cancelar", generate: "Generar Paquete (6 págs)", generating: "Generando...",
      lang_label: "Idioma de la garantía",
      need_name: "Por favor ingrese el nombre del comprador",
      ok: "¡Paquete generado!", err: "Error al generar el paquete",
    },
  })[language];

  // Setup canvas on mount
  const generate = async () => {
    if (!form.buyer_name?.trim()) {
      toast.error(L.need_name);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        `/vehicles/${vehicle.id}/lemon-law-warranty.pdf`,
        {
          buyer_name: form.buyer_name,
          buyer_phone: form.buyer_phone,
          buyer_email: form.buyer_email,
          buyer_address: form.buyer_address,
          sale_price: Number(form.sale_price) || 0,
          sale_date: form.sale_date,
          mileage: Number(form.mileage) || 0,
          // No buyer_signature_data_url — the customer signs the printed
          // paper after the dealer walks through the warranty.
          language,
        },
        { responseType: "blob" }
      );
      openPdfBlob(res.data, "garantia-lemon-law.pdf");
      toast.success(L.ok);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || L.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-8 px-4">
      <div
        data-testid="lemon-law-modal"
        className="bg-background border border-primary w-full max-w-3xl p-6 space-y-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-primary" />
              <p className="label-eyebrow text-primary">{L.header_eyebrow}</p>
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">
              {L.header_title}
            </h2>
            <p className="text-xs text-text-secondary mt-1">{L.header_desc}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="lemon-law-close">
            <X size={22} className="text-text-secondary hover:text-primary" />
          </button>
        </div>

        {/* Language selector */}
        <div>
          <p className="label-eyebrow text-text-secondary mb-2">{L.lang_label}</p>
          <div className="inline-flex border border-border bg-surface p-0.5">
            {[
              { code: "pt", label: "Português", flag: "PT" },
              { code: "en", label: "English", flag: "EN" },
              { code: "es", label: "Español", flag: "ES" },
            ].map((opt) => (
              <button
                key={opt.code}
                type="button"
                data-testid={`ll-lang-${opt.code}`}
                onClick={() => setLanguage(opt.code)}
                className={`px-4 h-9 text-xs font-display font-bold uppercase tracking-widest transition-colors ${
                  language === opt.code
                    ? "bg-primary text-white"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                <span className="mr-1.5 opacity-60">{opt.flag}</span>{opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Vehicle summary — Milhas is editable because the dealer test-drives
            the car before handover and the delivery mileage differs from the
            inventory value. Changing it instantly recalculates the coverage. */}
        <div className="border border-border bg-surface p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="label-eyebrow text-text-secondary">{L.vehicle}</p>
            <p className="font-display font-bold">{vehicle.year} {vehicle.make} {vehicle.model}</p>
          </div>
          <div>
            <p className="label-eyebrow text-text-secondary">VIN</p>
            <p className="font-mono text-[11px]">{vehicle.vin || "—"}</p>
          </div>
          <div>
            <label className="label-eyebrow text-primary block mb-1">{L.mileage}</label>
            <input
              data-testid="ll-mileage"
              type="number"
              min="0"
              step="1"
              value={form.mileage}
              onChange={(e) => set("mileage", e.target.value)}
              className="w-full bg-background border border-primary/30 focus:border-primary focus:outline-none px-2 h-8 text-sm font-display font-bold"
            />
          </div>
          <div>
            <p className="label-eyebrow text-text-secondary">{L.coverage}</p>
            <p className={`font-display font-bold ${term.color}`}>{term.label}</p>
          </div>
        </div>

        {!term.eligible && (
          <div className="border border-warning bg-warning/10 p-3 text-xs text-warning">
            {L.not_covered_warn}
          </div>
        )}

        {/* Buyer fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">{L.buyer_name}</label>
            <input
              data-testid="ll-buyer-name"
              required
              value={form.buyer_name}
              onChange={(e) => set("buyer_name", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">{L.phone}</label>
            <input
              data-testid="ll-buyer-phone"
              value={form.buyer_phone}
              onChange={(e) => set("buyer_phone", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">{L.email}</label>
            <input
              data-testid="ll-buyer-email"
              type="email"
              value={form.buyer_email}
              onChange={(e) => set("buyer_email", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">{L.address}</label>
            <input
              data-testid="ll-buyer-address"
              value={form.buyer_address}
              onChange={(e) => set("buyer_address", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">{L.sale_date}</label>
            <input
              data-testid="ll-sale-date"
              type="date"
              value={form.sale_date}
              onChange={(e) => set("sale_date", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
        </div>

        {/* Info hint — customer signs on the printed paper after walkthrough */}
        <div className="border border-primary/30 bg-primary/5 p-3 text-xs text-text-secondary leading-relaxed">
          <span className="font-display font-bold text-primary mr-1">i</span>
          {L.print_hint}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="border border-border hover:border-text-primary px-5 h-11 text-xs font-display font-bold uppercase tracking-widest"
          >
            {L.cancel}
          </button>
          <button
            type="button"
            data-testid="ll-generate"
            onClick={generate}
            disabled={busy}
            className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 h-11 text-xs font-display font-bold uppercase tracking-widest text-white inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
            {busy ? L.generating : L.generate}
          </button>
        </div>
      </div>
    </div>
  );
}
